import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderPlaidLinkToken } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import {
    getPlaidError,
    getPlaidFetching,
    getPlaidLinkToken,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { BankAccount } from '@/types/bankAccount';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import BankAccountOnFileWithPlaidVerify from './BankAccountOnFileWithPlaidVerify';

type Props = {
    bankAccount: BankAccount;
    className?: string;
    deleteAccount?: () => void;
};

/**
 * This component has a container because we cannot call the usePlaid link unless we have a token, or it will fail.
 */
const BankAccountOnFileWithPlaidVerifyContainerComponent = ({ bankAccount, ...rest }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const bidderId = useAppSelector(getBidderId);
    const { plaidToken: accessToken } = bankAccount;

    const plaidLinkToken = useAppSelector((state) => getPlaidLinkToken(state, accessToken));
    const plaidFetching = useAppSelector(getPlaidFetching);
    const plaidError = useAppSelector(getPlaidError);

    useEffect(() => {
        if (!plaidError && !plaidLinkToken && !plaidFetching && bidderId > 0 && accessToken !== '') {
            dispatch(
                fetchBidderPlaidLinkToken({
                    accessToken,
                    bidderId,
                })
            );
        }
    }, [accessToken, bidderId, dispatch, plaidError, plaidFetching, plaidLinkToken]);

    if (plaidFetching) {
        return (
            <div
                className="ml-sm flex"
                data-testid="bankaccount-on-file-with-plaid-loading"
            >
                <Loading />
            </div>
        );
    }

    if (plaidError) {
        return <div className="ml-sm flex">{formatMessage(['plaid', 'errorLoading'])}</div>;
    }

    if (plaidLinkToken) {
        return (
            <BankAccountOnFileWithPlaidVerify
                bankAccount={bankAccount}
                {...rest}
            />
        );
    }

    return null;
};

export const BankAccountOnFileWithPlaidVerifyContainer = BankAccountOnFileWithPlaidVerifyContainerComponent;
